<template>
  <v-dialog v-model="showDialog" max-width="500" persistent @keydown.esc="close">
    <v-card>
      <v-card-title class="primaryDialogTitle">
        <span class="headline">{{ isNew ? 'Create New Booking' : 'Edit Booking' }}</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-form>
          <ValidationObserver ref="form" v-slot="{ invalid }">
            <!-- Clinic Selection (only for admin) -->
            <v-row v-if="!isClinicStaff">
              <v-col cols="12">
                <ValidationProvider v-slot="{ errors }" name="Clinic" rules="required">
                  <FletrixAutocomplete
                    label="*Clinic"
                    placeholder="Clinic Name"
                    path="rml/admin/ClinicProfiles"
                    :params="{
                      sort: { created_at: -1 },
                      filter: { membership: 'vip' },
                    }"
                    item-value="_id"
                    v-model="formData.clinic"
                    :clearable="true"
                    ref="autoComplete"
                    @change="onClinicChange"
                  >
                    <template v-slot:item="data">
                      <span>{{ data.item.name | parseAndLocalize('en') }}</span>
                    </template>
                    <template v-slot:selection="data">
                      <span>{{ data.item.name | parseAndLocalize('en') }}</span>
                    </template>
                  </FletrixAutocomplete>
                </ValidationProvider>
              </v-col>
            </v-row>

            <!-- Service and Doctor Selection -->
            <v-row>
              <v-col cols="12">
                <ValidationProvider v-slot="{ errors }" name="Service" rules="required">
                  <FletrixSelect
                    v-model="formData.serviceDetails.service"
                    :items="services"
                    :loading="loadingServices"
                    :disabled="!formData.clinic"
                    :error-messages="errors"
                    item-text="name"
                    item-value="_id"
                    @change="onServiceChange"
                    label="*Service"
                  >
                    <template v-slot:item="{ item }">
                      {{ parseAndLocalize(item.name, 'en') }}
                    </template>
                    <template v-slot:selection="{ item }">
                      {{ parseAndLocalize(item.name, 'en') }}
                    </template>
                  </FletrixSelect>
                </ValidationProvider>
              </v-col>

              <v-col cols="12">
                <ValidationProvider v-slot="{ errors }" name="Doctor" rules="required">
                  <FletrixSelect
                    v-model="formData.serviceDetails.doctor"
                    :items="doctors"
                    :loading="loadingDoctors"
                    :disabled="!formData.clinic"
                    :error-messages="errors"
                    item-text="fullName"
                    item-value="_id"
                    clearable
                    @change="onDoctorChange"
                    label="*Doctor"
                  >
                    <template v-slot:item="{ item }">
                      {{ getDoctorFullName(item) }}
                    </template>
                    <template v-slot:selection="{ item }">
                      {{ getDoctorFullName(item) }}
                    </template>
                  </FletrixSelect>
                </ValidationProvider>
              </v-col>
            </v-row>

            <!-- Approval Status -->
            <v-row>
              <v-col cols="12">
                <ValidationProvider v-slot="{ errors }" name="Approval Status" rules="required">
                  <FletrixSelect
                    v-model="formData.approvalStatus"
                    :items="bookingApprovalStatusOptions"
                    :error-messages="errors"
                    item-text="text"
                    item-value="value"
                    label="Status"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>

            <!-- Patient Information -->
            <v-row>
              <v-col cols="12">
                <v-divider></v-divider>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <ValidationProvider v-slot="{ errors }" name="Patient">
                  <FletrixAutocomplete
                    label="Search Patient"
                    placeholder="Search by name or contact number"
                    path="rml/admin/patients"
                    :params="{
                      filter: memberId ? { memberId: memberId } : {},
                    }"
                    item-value="_id"
                    v-model="selectedPatient"
                    :clearable="true"
                    @change="onPatientChange"
                    :error-messages="errors"
                  >
                    <template v-slot:item="data">
                      <span
                        >{{ data.item.name_first }} {{ data.item.name_last }}
                        {{ data.item.contactNumber }}</span
                      >
                    </template>
                    <template v-slot:selection="data">
                      <span
                        >{{ data.item.name_first }} {{ data.item.name_last }}
                        {{ data.item.contactNumber }}</span
                      >
                    </template>
                  </FletrixAutocomplete>
                </ValidationProvider>
              </v-col>
            </v-row>

            <v-row v-if="!selectedPatient">
              <v-col cols="6">
                <ValidationProvider
                  v-slot="{ errors }"
                  name="First Name"
                  :rules="{ required: !selectedPatient }"
                >
                  <FletrixTextField
                    label="First Name"
                    v-model="formData.guest.name_first"
                    :error-messages="errors"
                  />
                </ValidationProvider>
              </v-col>

              <v-col cols="6">
                <ValidationProvider
                  v-slot="{ errors }"
                  name="Last Name"
                  :rules="{ required: !selectedPatient }"
                >
                  <FletrixTextField
                    label="Last Name"
                    v-model="formData.guest.name_last"
                    :error-messages="errors"
                  />
                </ValidationProvider>
              </v-col>

              <v-col cols="12">
                <ValidationProvider v-slot="{ errors }" name="Age Range">
                  <FletrixSelect
                    label="Age Range"
                    v-model="formData.guest.age"
                    :items="ageRanges"
                    :error-messages="errors"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>

            <v-row v-if="!selectedPatient">
              <v-col cols="12">
                <!-- <phone-validator
                ref="phoneValidator"
                :phone.sync="formData.guest.phone"
                default-country-code="852"
                :rules="{ required: !selectedPatient }"
              /> -->
                <ValidationProvider
                  v-slot="{ errors }"
                  name="Contact No"
                  :rules="{ required: !selectedPatient }"
                >
                  <FletrixPhone
                    label="Contact No"
                    v-model="formData.guest.phone"
                    :error-messages="errors"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>

            <!-- Date Selection -->
            <v-row v-if="!hasPreselectedTime">
              <v-col cols="12">
                <ValidationProvider v-slot="{ errors }" name="Date" rules="required">
                  <v-menu
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <FletrixTextField
                        v-model="selectedDate"
                        prepend-icon="mdi-calendar"
                        :error-messages="errors.concat(dateError || [])"
                        :error="!!dateError"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        label="Date"
                      />
                    </template>
                    <v-date-picker v-model="selectedDate" @input="onDateChange" :min="minDate" />
                  </v-menu>
                </ValidationProvider>
              </v-col>
            </v-row>

            <!-- Preselected Date and Time Display -->
            <v-row v-if="hasPreselectedTime && selectedTimeSlot">
              <v-col cols="12">
                <FletrixTextField
                  label="Date"
                  :value="selectedDate | moment('YYYY-MM-DD')"
                  prepend-icon="mdi-calendar"
                  readonly
                />
              </v-col>
              <v-col cols="12">
                <FletrixTextField
                  label="Time Slot"
                  :value="formattedTimeSlot"
                  prepend-icon="mdi-clock-outline"
                  readonly
                />
              </v-col>
            </v-row>

            <!-- Time Slot Selection (only show if no preselected time) -->
            <v-row
              v-if="
                !hasPreselectedTime &&
                selectedDate &&
                formData.clinic &&
                formData.serviceDetails.doctor &&
                !dateError
              "
            >
              <v-col cols="12">
                <ValidationProvider v-slot="{ errors }" name="Time Slot" rules="required">
                  <FletrixSelect
                    label="Available Time Slot"
                    v-model="selectedTimeSlot"
                    :items="availableSlots"
                    :loading="loadingSlots"
                    :disabled="!availableSlots.length || !!dateError"
                    :error-messages="errors"
                    item-text="display"
                    return-object
                    :no-data-text="slotsMessage"
                  >
                    <template v-slot:item="{ item, on }">
                      <v-list-item v-on="on">
                        <v-list-item-content>
                          <v-list-item-title :class="{ 'error--text': !item.available }">
                            {{ item.display }}
                            <v-tooltip bottom v-if="get(item, 'errors.length')">
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon color="primary" dark v-bind="attrs" v-on="on">
                                  mdi-alert-circle
                                </v-icon>
                              </template>
                              <span>{{ item.errors.join(' ; ') }}</span>
                            </v-tooltip>
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </FletrixSelect>
                </ValidationProvider>
              </v-col>
            </v-row>
          </ValidationObserver>
        </v-form>
      </v-card-text>

      <!-- Action Buttons -->
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn color="primary" :loading="loading" :disabled="shouldDisableSubmit" @click="save">
          {{ isNew ? 'Create' : 'Update' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api';
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import { debounce } from 'lodash';
import { bookingApprovalStatusOptions, ageRangesOption } from '@/data/optionsConstants';
import { get } from 'lodash';
import { RESTFUL } from '@/data/constants';
import { parseAndLocalize } from '@/services/filters';

import {
  FletrixAutocomplete,
  FletrixTextField,
  FletrixSelect,
  FletrixPhone,
} from '@/components/Atom';
import { crudRead } from '@/api/crud';

export default {
  name: 'BookingEditDialog',
  components: {
    FletrixAutocomplete,
    FletrixTextField,
    FletrixSelect,
    FletrixPhone,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: 'Booking',
    },
    preselectedDate: {
      type: Date,
      default: null,
    },
    preselectedTime: {
      type: Object,
      default: null,
    },
    preselectedClinic: {
      type: String,
      default: null,
    },
    memberId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      dateMenu: false,
      selectedDate: null,
      selectedTimeSlot: null,
      selectedPatient: null,
      clinic: null,
      availableSlots: [],
      loadingSlots: false,
      slotsMessage: 'Select date and clinic to view available slots',
      formData: {
        clinic: null,
        serviceDetails: {
          service: null,
          doctor: null,
          patient: null,
        },
        guest: {
          name_first: '',
          name_last: '',
          phone: '',
          age: null,
        },
        approvalStatus: 'pending',
      },
      services: [],
      loadingServices: false,
      doctors: [],
      loadingDoctors: false,
      ageRanges: ageRangesOption,
      invalid: false,
      bookingApprovalStatusOptions,
      dateError: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['isClinicStaff', 'clinicId']),
    showDialog: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit('input', v);
      },
    },
    isNew() {
      return !this.id || this.id === 'new';
    },
    minDate() {
      return new Date().toISOString().substr(0, 10);
    },
    hasPreselectedTime() {
      return !!(
        this.preselectedDate &&
        this.preselectedTime &&
        this.preselectedTime.start &&
        this.preselectedTime.end
      );
    },
    formattedTimeSlot() {
      if (!this.selectedTimeSlot) return '';
      // Handle both direct time object and slot object with value property
      const start = this.selectedTimeSlot.value?.start || this.selectedTimeSlot.start;
      const end = this.selectedTimeSlot.value?.end || this.selectedTimeSlot.end;
      return `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`;
    },
    shouldDisableSubmit() {
      return (
        !this.hasPreselectedTime &&
        this.selectedDate &&
        !this.loadingSlots &&
        (this.availableSlots.length === 0 || !!this.dateError)
      );
    },
  },
  watch: {
    showDialog(val) {
      if (val) {
        this.initialize();
      }
    },
  },
  methods: {
    get,
    moment,
    parseAndLocalize,
    ...mapActions('alert', ['updateAlertMessage']),

    async initialize() {
      this.resetForm();

      // Set default clinic for clinic staff or preselected clinic for admin
      if (this.isClinicStaff && this.clinicId) {
        this.formData.clinic = this.clinicId;
        await this.onClinicChange(this.clinicId);
      } else if (this.preselectedClinic) {
        // For admin users with preselected clinic from timetable
        this.formData.clinic = this.preselectedClinic;
        await this.onClinicChange(this.preselectedClinic);
      }

      if (!this.isNew) {
        await this.loadBooking();
      } else {
        // Handle preselected time from timetable
        if (this.hasPreselectedTime) {
          this.selectedDate = new Date(this.preselectedDate).toISOString().substr(0, 10);
          this.selectedTimeSlot = {
            start: this.preselectedTime.start,
            end: this.preselectedTime.end,
          };
          // Load available slots to validate the preselected time
          if (this.formData.clinic) {
            await this.loadAvailableSlots();
            // Try to find matching slot in available slots
            const matchingSlot = this.availableSlots.find(
              slot =>
                slot.value.start === this.preselectedTime.start &&
                slot.value.end === this.preselectedTime.end,
            );
            if (matchingSlot) {
              this.selectedTimeSlot = matchingSlot;
            }
          }
        } else {
          // Set default date to today
          this.selectedDate = new Date().toISOString().substr(0, 10);
        }
      }
    },

    resetForm() {
      this.selectedDate = null;
      this.selectedTimeSlot = null;
      this.selectedPatient = null;
      this.availableSlots = [];
      this.dateError = null;
      this.formData = {
        clinic: null,
        serviceDetails: {
          service: null,
          doctor: null,
          patient: null,
        },
        guest: {
          name_first: '',
          name_last: '',
          phone: '',
          age: null,
        },
        approvalStatus: 'pending',
      };
      this.$refs.form?.reset();
    },

    async loadBooking() {
      try {
        this.loading = true;
        const response = await api.get(`rml/admin/bookings/${this.id}`);
        const booking = response.data;

        // Parse booking data
        this.formData.clinic = booking.clinic?._id || booking.clinic;
        this.formData.serviceDetails.service =
          booking.serviceDetails?.service?._id || booking.serviceDetails?.service;
        this.formData.serviceDetails.doctor =
          booking.serviceDetails?.doctor?._id || booking.serviceDetails?.doctor;
        this.formData.serviceDetails.patient =
          booking.serviceDetails?.patient?._id || booking.serviceDetails?.patient;
        this.formData.guest = booking.guest || {
          name_first: '',
          name_last: '',
          phone: '',
          age: null,
        };
        this.formData.approvalStatus = booking.approvalStatus || 'pending';

        // Set selectedPatient if serviceDetails.patient exists
        if (this.formData.serviceDetails.patient) {
          this.selectedPatient = this.formData.serviceDetails.patient;
        }

        // Parse time
        if (booking.time) {
          const startMoment = moment(booking.time.start);
          const endMoment = moment(booking.time.end);
          this.selectedDate = startMoment.format('YYYY-MM-DD');
          // Load available slots for this date first
          await this.loadAvailableSlots();
          // Then find and set the matching slot
          const matchingSlot = this.availableSlots.find(
            slot => slot.value.start === booking.time.start && slot.value.end === booking.time.end,
          );
          if (matchingSlot) {
            this.selectedTimeSlot = matchingSlot;
          }
        }

        // Load related data
        if (this.formData.clinic) {
          await this.loadServices();
          await this.loadDoctors();
        }
      } catch (error) {
        console.error('Error loading booking:', error);
        this.updateAlertMessage({
          msg: 'Failed to load booking details',
          type: 'error',
        });
      } finally {
        this.loading = false;
      }
    },

    async onClinicChange(clinicId) {
      // Reset dependent fields
      this.formData.serviceDetails.service = null;
      this.formData.serviceDetails.doctor = null;
      this.services = [];
      this.doctors = [];
      this.selectedTimeSlot = null;
      this.availableSlots = [];
      this.dateError = null;

      this.clinic = await crudRead(api, RESTFUL.clinicProfile.read, clinicId, {
        populate: [{ path: 'serviceProducts', select: ['_id', 'name'] }],
      });

      if (this.formData.clinic) {
        await Promise.all([this.loadServices(), this.loadDoctors()]);
        // Load available slots if date is selected
        if (this.selectedDate) {
          await this.loadAvailableSlots();
        }
      }
    },

    async onDateChange(date) {
      this.dateMenu = false;
      this.selectedTimeSlot = null;
      this.availableSlots = [];
      this.dateError = null;
      if (date && this.formData.clinic && this.formData.serviceDetails.doctor) {
        await this.loadAvailableSlots();
      }
    },

    async loadAvailableSlots() {
      if (!this.selectedDate || !this.formData.clinic || !this.formData.serviceDetails.doctor) {
        return;
      }

      try {
        this.loadingSlots = true;
        this.slotsMessage = 'Loading available slots...';

        const payload = {
          date: moment(this.selectedDate).startOf('day').format(),
          clinic: this.formData.clinic,
        };

        // Add doctor if selected
        if (this.formData.serviceDetails.doctor) {
          payload.doctor = this.formData.serviceDetails.doctor;
        }

        const response = await api.get('rml/admin/booking/availableSlots', {
          params: payload,
        });

        const slots = response.data || [];

        // Format all slots (show both available and unavailable)
        this.availableSlots = slots.map(slot => {
          const startMoment = moment(slot.start);
          const endMoment = moment(slot.end);
          return {
            display: `${startMoment.format('HH:mm')} - ${endMoment.format('HH:mm')}`,
            value: {
              start: slot.start,
              end: slot.end,
            },
            occupied: slot.slotCapacityInfo?.occupied || 0,
            maxCapacity: slot.slotCapacityInfo?.maxCapacity || 1,
            available: slot.available,
            errors: slot.slotCapacityInfo?.errors || [],
          };
        });

        if (this.availableSlots.length === 0) {
          this.slotsMessage = 'No available slots for selected date';
          this.dateError = 'No available slots for selected date';
        } else {
          const allSlotsHaveErrors = this.availableSlots.every(
            slot => !slot.available || slot.errors.length > 0,
          );
          if (allSlotsHaveErrors) {
            this.dateError = 'No available slots for selected date';
          } else {
            this.dateError = null;
          }
        }
      } catch (error) {
        console.error('Error loading available slots:', error);
        this.slotsMessage = 'Failed to load available slots';
        this.availableSlots = [];
        this.dateError = null;
      } finally {
        this.loadingSlots = false;
      }
    },

    async loadServices() {
      if (!this.clinic) return;
      this.services = this.clinic.serviceProducts || [];
    },

    async loadDoctors() {
      if (!this.formData.clinic) return;

      try {
        this.loadingDoctors = true;
        const response = await api.get('rml/admin/DoctorProfiles', {
          params: {
            filter: { clinic: this.formData.clinic, enabled: true },
            limit: 1000,
          },
        });
        this.doctors = response.data.docs || [];
      } catch (error) {
        console.error('Error loading doctors:', error);
      } finally {
        this.loadingDoctors = false;
      }
    },

    getDoctorFullName(doctor) {
      if (!doctor) return '';
      const firstName = this.parseAndLocalize(doctor.firstName);
      const lastName = this.parseAndLocalize(doctor.lastName);
      return `Dr. ${firstName} ${lastName}`.trim();
    },

    onServiceChange() {
      // Reset doctor if selected service changes
      this.formData.serviceDetails.doctor = null;
      // Reload slots if date is selected
      if (this.selectedDate && this.formData.clinic && this.formData.serviceDetails.doctor) {
        this.loadAvailableSlots();
      }
    },

    onDoctorChange() {
      // Reload slots when doctor changes
      // Don't reset selectedTimeSlot if it's preselected from timetable
      if (!this.hasPreselectedTime) {
        this.selectedTimeSlot = null;
        this.dateError = null;
        if (this.selectedDate && this.formData.clinic && this.formData.serviceDetails.doctor) {
          this.loadAvailableSlots();
        }
      }
    },

    onPatientChange(patient) {
      if (patient) {
        this.formData.guest.name_first = patient.name_first;
        this.formData.guest.name_last = patient.name_last;
        this.formData.guest.phone = patient.contactNumber;
        this.formData.guest.age = patient.age;
      } else {
        this.formData.guest.name_first = '';
        this.formData.guest.name_last = '';
        this.formData.guest.phone = '';
        this.formData.guest.age = null;
      }
    },

    async save() {
      const isValid = await this.$refs.form.validate();
      if (!isValid) return;

      // Validate phone
      if (this.$refs.phoneValidator) {
        const phoneValid = await this.$refs.phoneValidator.$refs.form?.validate();
        if (!phoneValid) return;
      }

      // Validate time slot selection
      if (!this.selectedTimeSlot) {
        this.updateAlertMessage({
          msg: 'Please select a time slot',
          type: 'error',
        });
        return;
      }

      try {
        this.loading = true;

        // Prepare payload
        const payload = {
          clinic: this.formData.clinic,
          serviceDetails: {
            service: this.formData.serviceDetails.service,
          },
          guest: {
            name_first: this.formData.guest.name_first,
            name_last: this.formData.guest.name_last,
            phone: this.formData.guest.phone,
            age: this.formData.guest.age,
            memberId: this.memberId,
          },
          time: {
            // Handle both preselected time (direct object) and selected from dropdown (has value property)
            start: this.selectedTimeSlot.value?.start || this.selectedTimeSlot.start,
            end: this.selectedTimeSlot.value?.end || this.selectedTimeSlot.end,
          },
          approvalStatus: this.formData.approvalStatus,
          isSkipChecking: true,
        };

        // Add optional doctor field
        if (this.formData.serviceDetails.doctor) {
          payload.serviceDetails.doctor = this.formData.serviceDetails.doctor;
        }

        // Add patient if selected from autocomplete
        if (this.selectedPatient) {
          payload.serviceDetails.patient = this.selectedPatient._id || this.selectedPatient;
          delete payload.guest;
        }

        let result;
        if (this.isNew) {
          const response = await api.post('rml/admin/bookings', payload);
          result = response.data;
          this.updateAlertMessage({
            msg: 'Booking created successfully',
            type: 'success',
            color: 'success',
          });
          this.$emit('create', result);
        } else {
          const response = await api.patch(`rml/admin/bookings/${this.id}`, payload);
          result = response.data;
          this.updateAlertMessage({
            msg: 'Booking updated successfully',
            type: 'success',
            color: 'success',
          });
          this.$emit('update', result);
        }

        this.close();
      } catch (error) {
        console.error('Error saving booking:', error);
        this.updateAlertMessage({
          msg: `Failed to ${this.isNew ? 'create' : 'update'} booking`,
          type: 'error',
        });
      } finally {
        this.loading = false;
      }
    },

    close() {
      this.showDialog = false;
    },
  },
};
</script>

<style scoped>
.redRemind {
  position: absolute;
  color: red;
  font-size: 12px;
}
</style>
